import { ArrowRight2Icon } from "@/assets/icons";
import { cn } from "@/lib/utils";
import type { ComponentType, SVGProps } from "react";

export type PersonaAccent = "blue" | "purple" | "teal";

type AccentStyles = {
  background: string;
  border: string;
  activeBorder: string;
  activeTitle: string;
  iconBackground: string;
  iconColor: string;
  hoverShadow: string;
  ring: string;
};

const ACCENTS: Record<PersonaAccent, AccentStyles> = {
  blue: {
    background:
      "linear-gradient(180deg, rgba(255,255,255,0.6) 50%, #D3E0FB 100%)",
    border: "border-[rgba(48,66,226,0.2)]",
    activeBorder: "border-blue-600",
    activeTitle: "text-blue-600",
    iconBackground: "bg-[#DDE7FC]",
    iconColor: "text-blue-600",
    hoverShadow: "hover:shadow-[0_12px_28px_rgba(48,66,226,0.16)]",
    ring: "focus-visible:ring-blue-600",
  },
  purple: {
    background:
      "linear-gradient(180deg, rgba(255,255,255,0.6) 50%, #F5F2FD 100%)",
    border: "border-[rgba(92,16,214,0.12)]",
    activeBorder: "border-purple-7",
    activeTitle: "text-purple-7",
    iconBackground: "bg-[#EDE6FB]",
    iconColor: "text-purple-7",
    hoverShadow: "hover:shadow-[0_12px_28px_rgba(92,16,214,0.16)]",
    ring: "focus-visible:ring-purple-7",
  },
  teal: {
    background:
      "linear-gradient(180deg, rgba(255,255,255,0.6) 50%, #EAF9FC 100%)",
    border: "border-[rgba(0,182,206,0.25)]",
    activeBorder: "border-[#00B6CE]",
    activeTitle: "text-[#0B7A8C]",
    iconBackground: "bg-[#DCF3F7]",
    iconColor: "text-[#0B7A8C]",
    hoverShadow: "hover:shadow-[0_12px_28px_rgba(0,182,206,0.16)]",
    ring: "focus-visible:ring-[#00B6CE]",
  },
};

export type PersonaCardProps = {
  accent: PersonaAccent;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  title: string;
  description: string;
  active?: boolean;
  onSelect: () => void;
  onActivate?: () => void;
  disabled?: boolean;
  "data-testid"?: string;
};

export default function PersonaCard({
  accent,
  icon: Icon,
  title,
  description,
  active = false,
  onSelect,
  onActivate,
  disabled = false,
  "data-testid": dataTestId,
}: PersonaCardProps) {
  const styles = ACCENTS[accent];

  return (
    <button
      type="button"
      onClick={onSelect}
      onMouseEnter={onActivate}
      onFocus={onActivate}
      disabled={disabled}
      data-testid={dataTestId}
      style={{ backgroundImage: styles.background }}
      className={cn(
        "group relative flex h-[88px] w-full cursor-pointer items-center gap-3 rounded-[16px] border p-4 text-left transition-all",
        "md:h-[200px] md:max-w-[284px] md:flex-1 md:flex-col md:justify-center md:gap-4 md:p-6 md:text-center",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-default disabled:opacity-60",
        styles.hoverShadow,
        styles.ring,
        active ? styles.activeBorder : styles.border,
      )}
    >
      <span
        className={cn(
          "flex h-11 w-11 shrink-0 items-center justify-center rounded-full md:h-16 md:w-16",
          styles.iconBackground,
          styles.iconColor,
        )}
      >
        <Icon className="h-5 w-5 md:h-7 md:w-7" aria-hidden="true" />
      </span>

      <span className="flex min-w-0 flex-1 flex-col md:flex-none md:items-center md:gap-1">
        <span
          className={cn(
            "font-figtree text-[17px] font-extrabold leading-[1.2] tracking-[-0.2px] transition-colors md:text-[28px] md:tracking-[-0.5px]",
            active ? styles.activeTitle : "text-blue-900",
          )}
        >
          {title}
        </span>
        <span className="font-open-sans text-[13px] leading-[1.3] text-[#525966] md:text-[16px] md:leading-[1.35]">
          {description}
        </span>
      </span>

      <ArrowRight2Icon
        className="h-5 w-5 shrink-0 text-[#8A93A6] md:hidden"
        aria-hidden="true"
      />
    </button>
  );
}
